import React, { useState } from "react";
import { redeemReferralCode, redeemVoucher } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * RedeemCodePanel — violet "have a code?" card that lets users unlock
 * PRO without a card. Lives on `/pricing` (below the plan grid) AND
 * on the profile page (below the PRO/CASUAL tier cards) so the
 * code-redeem path is discoverable from either entry point.
 *
 * Props:
 *  • onToast       – toast callback from the page shell.
 *  • onRequireAuth – called when an unauthenticated user clicks REDEEM.
 *  • compact       – when true, shrinks the headline + padding for use
 *                    inside denser layouts (profile page). Default false.
 */
export function RedeemCodePanel({ onToast, onRequireAuth, compact = false }) {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");

  const submit = async () => {
    if (!user) return onRequireAuth?.();
    const trimmed = code.trim();
    if (trimmed.length < 4) {
      onToast?.("Enter a valid code first.");
      return;
    }
    setBusy(true);
    setOkMsg("");
    try {
      // Try the new admin-issued voucher system first (vouchers cover
      // most codes minted in /admin/vouchers). On 404/422 we fall back
      // to the legacy referral-grants flow so old codes keep working.
      try {
        const v = await redeemVoucher(trimmed.toUpperCase());
        setOkMsg(v.message || `🔥 ${v.plan?.toUpperCase()} unlocked for ${v.duration_days} days!`);
        setCode("");
        if (refresh) await refresh();
        return;
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        // Only fall back when the code clearly isn't a voucher.
        // A 410 (revoked/expired) or 409 (already redeemed) is the
        // final answer — surface it instead of pretending to retry.
        if (status && status !== 404 && status !== 422) {
          throw new Error(detail || "Couldn't redeem this code.");
        }
      }
      const r = await redeemReferralCode(trimmed);
      setOkMsg(
        r.kind === "pro_player"
          ? `🔥 PRO PLAYER unlocked for ${r.days} days!`
          : `🚀 PRO LEAGUE unlocked for ${r.days} days!`,
      );
      setCode("");
      if (refresh) await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      onToast?.(detail || "Couldn't redeem this code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="redeem-code-panel"
      className={`max-w-2xl mx-auto rounded-3xl border border-violet-400/30 bg-gradient-to-br from-[#1a0f2b] via-[#150a26] to-[#1a0f2b] ${
        compact ? "p-5" : "p-6"
      }`}
    >
      <p className="text-[10px] tracking-[3px] text-violet-300 font-bold text-center">
        <i className="fas fa-ticket mr-1" />HAVE A CODE?
      </p>
      <h3
        className={`font-display grad-text text-center mt-2 ${
          compact ? "text-xl" : "text-2xl sm:text-3xl"
        }`}
      >
        REDEEM FREE PRO
      </h3>
      <p
        className={`text-white/60 text-center mt-2 ${
          compact ? "text-xs" : "text-sm"
        }`}
      >
        Got a code from a creator, admin, or giveaway? Unlock PRO Player
        or PRO League instantly — no card needed.
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Paste your code"
          data-testid="redeem-code-input"
          maxLength={64}
          className="flex-1 bg-black/40 border border-violet-400/30 rounded-2xl px-4 py-3 text-white text-sm font-mono"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="redeem-code-submit"
          className="px-6 py-3 rounded-2xl bg-gradient-to-r from-violet-500 to-violet-700 text-white text-sm tracking-widest font-bold hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "…" : "REDEEM"}
        </button>
      </div>
      {okMsg && (
        <p
          data-testid="redeem-code-success"
          className="mt-3 text-center text-[#14b8a6] text-sm font-bold"
        >
          {okMsg}
        </p>
      )}
      <p className="mt-3 text-[10px] text-white/40 text-center">
        One-shot codes — each token can only be redeemed once.
      </p>
    </div>
  );
}

export default RedeemCodePanel;
